import type * as React from 'react';

import type { BrandIconId } from '../../assets/brandIcons';
import { ConnectedServiceIcon } from '../../atoms/ConnectedServiceIcon';
import { useHoverWithDebounce } from '../../hooks';
import { cn } from '../../lib/utils';
import { Donut, WidgetBreakdownPopover, type WidgetBreakdownItem } from '../../molecules';

const COVERED_BULLET_COLOR = '#FF5722'; // dawn-orange, matches Donut primary
const TOTAL_BULLET_COLOR = '#D9D9D9'; // grey-200, matches Donut track

export interface FilesCoveredBodyProps {
  coveragePercent: number;
  filesCovered: number;
  totalFiles: number;
  filesCoveredBreakdown: readonly WidgetBreakdownItem[];
  totalFilesBreakdown: readonly WidgetBreakdownItem[];
  connectedServices: readonly BrandIconId[];
  /** Sentence-case label rendered under the covered count (e.g. "Files covered"). */
  coveredRowLabel: string;
  /** Sentence-case label rendered under the total count (e.g. "Total files"). */
  totalRowLabel: string;
  /** Title-case header for the covered-row breakdown popover (e.g. "Files Covered"). */
  coveredPopoverTitle: string;
  /** Title-case header for the total-row breakdown popover (e.g. "Total Files"). */
  totalPopoverTitle: string;
  /** Caption under the popover total describing the breakdown axis (e.g. "File Type"). */
  popoverCategoryLabel: string;
  /** Header above the connected-services icons (e.g. "Connected Services"). */
  connectedServicesLabel: string;
  /** Formatter applied to every number on the card and inside popovers. */
  formatNumber: (value: number) => string;
  /**
   * Storybook-only override that bypasses the hover debounce. Not consumed by
   * production wrappers. `'filesCovered'` forces the covered-row popover open.
   */
  forceHoverOpen?: 'filesCovered' | 'totalFiles';
  className?: string;
}

/**
 * Populated body for the "Files Covered" widget card.
 *
 * Layout: donut on the left, two numeric rows in the middle (stacked
 * number-then-label pairs per Figma node 5:17871), optional Connected Services
 * column on the right (label + wrapped service icons). Divider and services
 * column are omitted entirely when `connectedServices` is empty.
 *
 * Hover behavior: two independent `useHoverWithDebounce` instances, one per
 * row. Leaving a row closes its popover immediately; entering the other row
 * schedules the next open after the 300 ms debounce. Because only one row can
 * be hovered at a time, at most one popover is visible.
 *
 * `forceHoverOpen` is a Storybook-only override that bypasses the debounce so
 * designers can inspect the open popover without hovering. Production wrappers
 * never pass it.
 */
export const FilesCoveredBody: React.FC<FilesCoveredBodyProps> = ({
  coveragePercent,
  filesCovered,
  totalFiles,
  filesCoveredBreakdown,
  totalFilesBreakdown,
  connectedServices,
  coveredRowLabel,
  totalRowLabel,
  coveredPopoverTitle,
  totalPopoverTitle,
  popoverCategoryLabel,
  connectedServicesLabel,
  formatNumber,
  forceHoverOpen,
  className,
}) => {
  const coveredHover = useHoverWithDebounce();
  const totalHover = useHoverWithDebounce();

  const hasServices = connectedServices.length > 0;

  const showCoveredPopover =
    forceHoverOpen === 'filesCovered' || (forceHoverOpen === undefined && coveredHover.isHovered);
  const showTotalPopover =
    forceHoverOpen === 'totalFiles' || (forceHoverOpen === undefined && totalHover.isHovered);

  return (
    <div
      className={cn(
        'relative flex flex-wrap items-start gap-6 font-outfit text-dawn-black',
        className
      )}
    >
      {/* Donut + stats group — the two share a tighter 16px gap and together
          sit 24px away from the divider (outer gap on the flex parent). */}
      <div className="flex shrink-0 items-center gap-4">
        <Donut percent={coveragePercent} />

        <div className="flex w-[124px] shrink-0 flex-col gap-5">
          <div
            data-testid="files-covered-row"
            className="relative flex w-full items-start gap-2"
            onMouseEnter={coveredHover.onMouseEnter}
            onMouseLeave={coveredHover.onMouseLeave}
          >
            <span
              data-testid="files-covered-bullet"
              aria-hidden="true"
              className="inline-block h-2 w-2 shrink-0 rounded-[2px]"
              style={{ backgroundColor: COVERED_BULLET_COLOR }}
            />
            <div className="flex flex-1 flex-col gap-2">
              <div className="relative flex h-[17px] w-fit items-center gap-2">
                <span className="font-medium leading-[1.2] text-[24px] tabular-nums">
                  {formatNumber(filesCovered)}
                </span>

                {showCoveredPopover ? (
                  <div
                    data-testid="files-covered-popover"
                    className="absolute left-full top-0 z-30 pl-2"
                  >
                    <WidgetBreakdownPopover
                      title={coveredPopoverTitle}
                      total={formatNumber(filesCovered)}
                      categoryLabel={popoverCategoryLabel}
                      items={filesCoveredBreakdown}
                      formatCount={formatNumber}
                    />
                  </div>
                ) : null}
              </div>
              <span className="text-[13px] font-normal leading-none">{coveredRowLabel}</span>
            </div>
          </div>

          <div
            data-testid="total-files-row"
            className="relative flex w-full items-start gap-2"
            onMouseEnter={totalHover.onMouseEnter}
            onMouseLeave={totalHover.onMouseLeave}
          >
            <span
              data-testid="total-files-bullet"
              aria-hidden="true"
              className="inline-block h-2 w-2 shrink-0 rounded-[2px]"
              style={{ backgroundColor: TOTAL_BULLET_COLOR }}
            />
            <div className="flex flex-1 flex-col gap-2">
              <div className="relative flex h-[17px] w-fit items-center">
                <span className="font-medium leading-[1.2] text-[24px] tabular-nums">
                  {formatNumber(totalFiles)}
                </span>

                {showTotalPopover ? (
                  <div
                    data-testid="total-files-popover"
                    className="absolute left-full top-0 z-30 pl-2"
                  >
                    <WidgetBreakdownPopover
                      title={totalPopoverTitle}
                      total={formatNumber(totalFiles)}
                      categoryLabel={popoverCategoryLabel}
                      items={totalFilesBreakdown}
                      formatCount={formatNumber}
                    />
                  </div>
                ) : null}
              </div>
              <span className="text-[13px] font-normal leading-none">{totalRowLabel}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Connected Services column (only when there are services) */}
      {hasServices ? (
        // The divider and the column wrap below the donut together when even
        // their min-content width does not fit beside it: at a 1280px viewport
        // the 4-of-12 card is ~290px inside and donut + stats + gaps alone take
        // 289. `flex-1` (basis 0) makes the wrap test use min-content, not the
        // full-width title. `self-stretch` keeps the divider as tall as the
        // donut when they share a line.
        <div data-testid="services-group" className="flex flex-1 gap-6 self-stretch">
          <div
            data-testid="services-divider"
            aria-hidden="true"
            className="w-px shrink-0 self-stretch bg-grey-200"
          />
          <div data-testid="services-column" className="flex flex-col items-start gap-[14px]">
            {/* `leading-none` on the title trims the half-leading that pushes
                the text down inside a `1.2` line-box, so the title and icons
                sit a few pixels higher and match the y-positions in Figma node
                5:17908. */}
            <p className="text-[14px] font-medium capitalize leading-none">
              {connectedServicesLabel}
            </p>
            {/* Wraps after 4 icons (4 × 24 + 3 × 8 = 120) so 6 icons render as
                4 + 2, matching Figma node 5:17912 / 5:17930. */}
            <div className="flex max-w-[120px] flex-wrap items-center gap-x-2 gap-y-[18px]">
              {connectedServices.map((service) => (
                <ConnectedServiceIcon key={service} service={service} size={24} />
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

FilesCoveredBody.displayName = 'FilesCoveredBody';
